import { useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

// Simple celebration melody using Web Audio API
function playCelebrationSound() {
  try {
    const ctx = new AudioContext();
    const notes = [
      { freq: 523.25, start: 0, dur: 0.15 },    // C5
      { freq: 659.25, start: 0.15, dur: 0.15 },  // E5
      { freq: 783.99, start: 0.3, dur: 0.15 },   // G5
      { freq: 1046.50, start: 0.45, dur: 0.3 },  // C6
      { freq: 783.99, start: 0.6, dur: 0.1 },    // G5
      { freq: 1046.50, start: 0.7, dur: 0.5 },   // C6 (held)
    ];

    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });

    // Close context after melody finishes
    setTimeout(() => ctx.close(), 2000);
  } catch {
    // Audio playback not supported, silently ignore
  }
}

const SuccessCelebration = ({ type, onDone }: { type: "wish" | "chipIn" | "card"; onDone: () => void }) => {
  const fireConfetti = useCallback(() => {
    const duration = 2500;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ["hsl(348,40%,28%)", "#f5e6d3", "#e8d5c0", "#d4a574", "#c4956a"],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ["hsl(348,40%,28%)", "#f5e6d3", "#e8d5c0", "#d4a574", "#c4956a"],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    confetti({
      particleCount: 80,
      spread: 100,
      origin: { y: 0.6 },
      colors: ["hsl(348,40%,28%)", "#f5e6d3", "#e8d5c0", "#d4a574", "#c4956a"],
    });

    frame();
  }, []);

  useEffect(() => {
    fireConfetti();
    playCelebrationSound();
  }, [fireConfetti]);

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col items-center text-center px-8 space-y-6">
        {/* Animated check circle */}
        <motion.div
          className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
        >
          <motion.div
            className="w-14 h-14 rounded-full bg-primary flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.4 }}
          >
            <Check className="text-primary-foreground" size={28} strokeWidth={3} />
          </motion.div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="space-y-2"
        >
          <h2 className="text-3xl font-serif font-bold text-foreground">
            {type === "card" ? "Card Created! 🎉" : type === "wish" ? "Wish Sent!" : "You Chipped In!"}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
            {type === "card"
              ? "Your card is ready to share. Time to invite people to sign it!"
              : type === "wish"
                ? "Your message has been added to the card. They're going to love it."
                : "Your contribution is on its way. You're making this gift extra special."}
          </p>
        </motion.div>

        {/* Done button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.3 }}
        >
          <Button
            onClick={onDone}
            className="rounded-full px-10 h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90"
            size="lg"
          >
            Done
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SuccessCelebration;
